import type { Habit, HabitCompletion, HabitSlot } from '../types';
import type { MealPlan, PlanOccasion, PlanState } from './nutritionPlan';
import { occasionsOf, planDayOn, planIdOf, planInView, plannedFor } from './nutritionPlan';
import { slotsOf } from './habitSlots';
import { OCCASION_TYPES, occasionTypeOfName, readOccasionType, type Meal, type OccasionType } from './nutrition';

/**
 * ⭐ The Nutrition habit link (phase 3b-2, owner 2026-10-09). One eating habit is linked to
 * the meal plan; ticking an interval asks what you ate, and logging a planned meal on Today
 * ticks the interval. Both halves share one operation id.
 * ⭐⭐ (owner, 2026-10-18 — approved picks) The habit's intervals ARE the occasions of the plan
 * it follows: an interval's id is its occasion's id, its label the occasion's name. When the
 * running plan changes, or its occasions do, the intervals and the daily target change with
 * it (`syncEatingHabit`) — from that day; earlier days keep the intervals they had
 * (`Habit.slotHistory`), so past ticks keep counting. With no plan running it keeps the last
 * plan's occasions. ONE eating habit. Pure helpers only.
 */

/** The linked eating habit (the first active one, if several were ever linked). */
export function linkedEatingHabit(habits: readonly Habit[]): Habit | null {
  return habits.find(h => !h.archivedAt && h.disciplineAction?.type === 'nutrition') ?? null;
}

/** Can this habit be linked? Not archived, not a system habit, not driving another tracker. */
export function canLinkEatingHabit(h: Habit): boolean {
  return !h.archivedAt && !h.system && (!h.disciplineAction || h.disciplineAction.type === 'nutrition');
}

/** The plan the habit follows today: the one running today, else the one it last followed, else the plan in view. */
export function eatingPlanFor(h: Pick<Habit, 'disciplineAction'>, state: PlanState, today: string): MealPlan | null {
  const running = planDayOn(state, today)?.plan;
  if (running) return running;
  const lastId = h.disciplineAction?.planId;
  return (lastId ? state.plans.find(p => p.id === lastId && !p.archivedAt) : undefined) ?? planInView(state, today);
}

/** A plan's occasions as intervals — the same ids and names. */
export function occasionSlots(state: Pick<PlanState, 'plans' | 'occasions'>, planId: string): HabitSlot[] {
  return occasionsOf(state, planId).map(o => ({ id: o.id, label: o.name }));
}

const key = (s: string) => s.trim().toLowerCase();

/**
 * ⭐⭐ Bring the eating habit's intervals in line with the plan it follows today (owner,
 * 2026-10-18). Returns the patch for `updateHabit`, or null when it already matches.
 *   · The intervals become the plan's occasions, the daily target their count.
 *   · When the SET changes (not just a name), the intervals it had are kept for every day
 *     before today (`slotHistory`) — unless they only began today.
 *   · Today's ticks are carried over to the new intervals: by the older pairing for a habit
 *     linked before this change, else by the same name (one on each side); an untyped tick
 *     takes the first interval still open. Anything else stays as it was (history).
 *   · A plan with no occasions leaves the habit as it is.
 */
export function syncEatingHabit(h: Habit, state: PlanState, today: string): Partial<Habit> | null {
  const a = h.disciplineAction;
  if (a?.type !== 'nutrition') return null;
  const plan = eatingPlanFor(h, state, today);
  if (!plan) return null;
  const next = occasionSlots(state, plan.id);
  if (!next.length) return null;
  const cur = slotsOf(h) ?? [];
  const legacy = !!(a.planSlots || a.slotOccasions || a.slotKinds);
  const sameIds = cur.length === next.length && cur.every((s, i) => s.id === next[i].id);
  const sameLabels = sameIds && cur.every((s, i) => s.label === next[i].label);
  if (sameLabels && a.planId === plan.id && !legacy) return null;

  const patch: Partial<Habit> = { slots: next, targetPerDay: next.length, disciplineAction: { type: 'nutrition', planId: plan.id } };
  if (sameIds) return patch;

  // The set changes: earlier days keep what they had.
  const history = [...(h.slotHistory ?? [])];
  const startedToday = history.some(e => e.until === today);
  if (!startedToday) history.push(cur.length ? { until: today, slots: cur } : { until: today, slots: [], target: Math.max(1, Math.floor(h.targetPerDay ?? 1)) });
  patch.slotHistory = history;

  // Today's ticks, carried over.
  const nextIds = new Set(next.map(s => s.id));
  const pairing = legacy ? planMatches(h, plan.id, state).matches : {};
  const label = new Map(cur.map(s => [s.id, s.label]));
  const unique = (xs: readonly HabitSlot[], name: string) => {
    const hits = xs.filter(s => key(s.label) === key(name));
    return hits.length === 1 ? hits[0] : null;
  };
  const taken = new Set((h.completions ?? []).filter(c => c.date >= today && !c.bonus && c.slotId && nextIds.has(c.slotId)).map(c => c.slotId!));
  let changed = false;
  const completions = (h.completions ?? []).map((c): HabitCompletion => {
    if (c.date < today || c.bonus || (c.slotId && nextIds.has(c.slotId))) return c;
    let to: HabitSlot | null = null;
    const paired = c.slotId ? pairing[c.slotId] : null;
    if (paired) to = next.find(s => s.id === paired) ?? null;
    if (!to && (c.slotId || c.slot)) {
      const name = (c.slotId ? label.get(c.slotId) : undefined) ?? c.slot;
      if (name && (!c.slotId || unique(cur, name))) to = unique(next, name);
    }
    if (!to && !c.slotId && !c.slot) to = next.find(s => !taken.has(s.id)) ?? null;
    if (!to || taken.has(to.id)) return c;
    taken.add(to.id);
    changed = true;
    return { ...c, slotId: to.id, slot: to.label };
  });
  if (changed) patch.completions = completions;
  return patch;
}

/** Still in the older form — linked before 2026-10-18 and not yet moved over by `syncEatingHabit`. */
const olderForm = (h: Habit) => {
  const a = h.disciplineAction;
  return !!a && (!a.planId || !!(a.planSlots || a.slotOccasions || a.slotKinds));
};

/** The occasion an interval stands for on a date (null = none — a plain tick). */
export function occasionForSlotOn(h: Habit, slotId: string | null, state: PlanState, date: string): PlanOccasion | null {
  if (!slotId) return null;
  // ⭐ An interval IS its occasion (2026-10-18) — on whichever plan it belongs to.
  const own = state.occasions.find(o => o.id === slotId);
  if (own) return own;
  // An interval from before, not yet carried over: the older pairing, on the date's plan.
  if (!olderForm(h)) return null;
  const pd = planDayOn(state, date);
  if (!pd) return null;
  const id = planMatches(h, pd.plan.id, state).matches[slotId];
  return id ? occasionsOf(state, pd.plan.id).find(o => o.id === id) ?? null : null;
}

/** The interval that stands for an occasion — among the intervals the habit had on `date` (default: today's). */
export function slotForOccasionIn(h: Habit, occasionId: string, state: Pick<PlanState, 'plans' | 'occasions'>, slots: readonly HabitSlot[] | null = slotsOf(h)): HabitSlot | null {
  const own = (slots ?? []).find(s => s.id === occasionId);
  if (own) return own;
  if (!olderForm(h)) return null;
  const o = state.occasions.find(x => x.id === occasionId);
  const planId = o ? planIdOf(o, state.plans) : null;
  if (!planId) return null;
  const { matches } = planMatches(h, planId, state);
  return (slots ?? []).find(s => matches[s.id] === occasionId) ?? null;
}

/**
 * ⭐ "Breakfast: Eggs (2)" (owner, 2026-10-18): the meal planned for an interval's occasion on
 * a date, or null (nothing planned — the interval shows just its name).
 */
export function plannedMealForSlot(state: PlanState, meals: readonly Meal[], slotId: string, date: string): Meal | null {
  if (!state.occasions.some(o => o.id === slotId)) return null;
  const p = plannedFor(state.entries, slotId, date, planDayOn(state, date)?.day ?? null);
  return p ? meals.find(m => m.id === p.mealId) ?? null : null;
}

// ── The older pairing (before 2026-10-18) — read only to carry a linked habit's ticks over ──

/** An interval's type: set when the habit was made, else its occasion's in a hand-set map, else its name ("Snack"). */
function slotKind(h: Habit, slot: HabitSlot, occasions: readonly PlanOccasion[]): OccasionType | null {
  const set = h.disciplineAction?.slotKinds?.[slot.id];
  if (set) return readOccasionType(set);
  const ids = [h.disciplineAction?.slotOccasions?.[slot.id], ...Object.values(h.disciplineAction?.planSlots ?? {}).map(m => m[slot.id])];
  for (const id of ids) {
    const k = id ? occasions.find(o => o.id === id)?.kind : null;
    if (k) return k;
  }
  return occasionTypeOfName(slot.label);
}

/**
 * Which of a plan's occasions each OLD interval stood for: set by hand for the plan, the old
 * single map, else by type then order, else by a unique exact name — never by position.
 */
function planMatches(h: Habit, planId: string, state: Pick<PlanState, 'plans' | 'occasions'>): { matches: Record<string, string | null> } {
  const slots = slotsOf(h) ?? [];
  const occ = occasionsOf(state, planId);
  const live = new Set(occ.map(o => o.id));
  const pick = (m: Record<string, string | null | undefined>) =>
    Object.fromEntries(slots.map(s => [s.id, m[s.id] && live.has(m[s.id]!) ? m[s.id]! : null])) as Record<string, string | null>;

  const byHand = h.disciplineAction?.planSlots?.[planId];
  if (byHand) return { matches: pick(byHand) };
  const legacy = h.disciplineAction?.slotOccasions ?? {};
  if (Object.values(legacy).some(id => live.has(id))) return { matches: pick(legacy) };

  const matches: Record<string, string | null> = Object.fromEntries(slots.map(s => [s.id, null]));
  const used = new Set<string>();
  for (const t of OCCASION_TYPES) {
    const ss = slots.filter(s => slotKind(h, s, state.occasions) === t);
    const os = occ.filter(o => o.kind === t);
    ss.forEach((s, i) => { if (os[i]) { matches[s.id] = os[i].id; used.add(os[i].id); } });
  }
  const count = <T,>(xs: readonly T[], name: (x: T) => string) => { const n = new Map<string, number>(); xs.forEach(x => n.set(key(name(x)), (n.get(key(name(x))) ?? 0) + 1)); return n; };
  const slotNames = count(slots, s => s.label);
  const occNames = count(occ, o => o.name);
  for (const s of slots) {
    if (matches[s.id] || slotKind(h, s, state.occasions) || slotNames.get(key(s.label)) !== 1) continue;
    const o = occ.find(x => !used.has(x.id) && occNames.get(key(x.name)) === 1 && key(x.name) === key(s.label));
    if (o) { matches[s.id] = o.id; used.add(o.id); }
  }
  return { matches };
}
